import React, { useEffect, useState } from 'react';
import './Styles.css';
import Form from '../../../Elements/Form';
import TextInput from '../../../Elements/ids/InputElements/TextInput';
import { formatMessage } from '../../../Localization/formatMessage';
import Messages from '../../../Localization/Messages';
import CheckBox from '../../../Elements/ids/InputElements/CheckBox';
import {
  getRatesBasedOnModalMode,
  getModalTitleText,
  getRatesWhenUseBrokerCostChange,
  getRatesWhenUsePersonalCommissionChange,
  haveFieldsChanged,
  getRatesWhenUseMotorCommissionChange,
  validateFields,
  getInputFieldsProperties,
} from './Functions';
import { BrokerCostType } from '../../../Enums/BrokerCostType';
import { ServiceClient } from '../../../Services/ServiceClient';
import CustomerSearch from './CustomerSearch';
import Buttons from './Buttons';
import { bcrModalModeEnum } from '../CommissionPage';
import LoaderComponent from '../../../Elements/ids/Loader';
import { createServerErrorMessage, getErrorMessage } from '../../../Helpers/FormInputHelper';
import ErrorMessage from '../../../Elements/ErrorMessage';
import Modal from '../../../Elements/ids/Modal/Modal';

export const editBcrFormId = 'editBcrForm';

const EditBrokerCostRate = ({ bcr, modalMode, onSave, defaultBcr, closeEditView, existingCustomers }) => {
  const [editedBcr, setEditedBcr] = useState(getRatesBasedOnModalMode(bcr, modalMode));
  const [canSubmit, setCanSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    const validationErrors = validateFields(editedBcr, modalMode, existingCustomers);
    setErrors(validationErrors);
    setCanSubmit(
      validationErrors.length === 0
        ? modalMode === bcrModalModeEnum.editCustomerRate || modalMode === bcrModalModeEnum.editDefaultRate
          ? haveFieldsChanged(editedBcr, bcr, modalMode)
          : true
        : false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedBcr]);

  const onFormSave = event => {
    event.preventDefault();
    setIsLoading(true);

    if (modalMode === bcrModalModeEnum.createDefaultRate || modalMode === bcrModalModeEnum.editDefaultRate) {
      ServiceClient.setBrokerCostRates(
        editedBcr.agentCode,
        editedBcr,
        () => {
          onSave(editedBcr);
          setIsLoading(false);
        },
        error => {
          setIsLoading(false);
          setErrors([createServerErrorMessage(error, editBcrFormId)]);
        }
      );
      return;
    }
    ServiceClient.setBrokerCostRatesForCustomer(
      editedBcr.agentCode,
      editedBcr.customerNumber,
      editedBcr,
      () => {
        onSave(editedBcr);
        setIsLoading(false);
      },
      error => {
        setIsLoading(false);
        setErrors([createServerErrorMessage(error, editBcrFormId)]);
      }
    );
  };

  const onCustomerSelect = customerData => {
    setEditedBcr({ ...editedBcr, ...customerData });
  };

  return (
    <Modal
      titleText={getModalTitleText(modalMode, editedBcr.customerNumber)}
      closeView={closeEditView}
      buttons={<Buttons closeModal={closeEditView} canSubmit={canSubmit} />}>
      {isLoading ? (
        <LoaderComponent />
      ) : (
        <div className="commission-page_edit-bcr-container">
          <Form formId={editBcrFormId} onSubmit={onFormSave}>
            <div className="commission-page_edit-bcr-customer-details">
              {modalMode === bcrModalModeEnum.newCustomerRate && (
                <>
                  <CustomerSearch onSelect={onCustomerSelect} />
                  <ErrorMessage message={getErrorMessage('customerSearch', errors)} />
                  {errors.findIndex(error => error.inputId === 'customerSearch') === -1 && (
                    <div>
                      {formatMessage(Messages.commissionPage_selectedCustomer, {
                        customerNumber: editedBcr.customerNumber,
                        customerName: editedBcr.customerName,
                        legalNumber: editedBcr.legalNumber,
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="commission-page_edit-bcr-form">
              <div>
                {modalMode !== bcrModalModeEnum.createDefaultRate && modalMode !== bcrModalModeEnum.newCustomerRate && (
                  <CheckBox
                    cssClasses="commission-page_edit-bcr_checkboxes"
                    checkBoxId="mappedToIfLogin"
                    checked={editedBcr.mappedToIfLogin ?? false}
                    disabled={true}
                    label={formatMessage(Messages.commissionPage_mappedToIfLogin)}
                  />
                )}

                {(modalMode === bcrModalModeEnum.editCustomerRate ||
                  modalMode === bcrModalModeEnum.newCustomerRate) && (
                  <CheckBox
                    cssClasses="commission-page_edit-bcr_checkboxes"
                    checkBoxId={'inheritCommissionHandling'}
                    checked={editedBcr.inheritCommissionHandling}
                    onChange={() => {
                      if (!editedBcr.inheritCommissionHandling) {
                        setEditedBcr({
                          ...defaultBcr,
                          customerName: editedBcr.customerName,
                          customerNumber: editedBcr.customerNumber,
                          legalNumber: editedBcr.legalNumber,
                          inheritCommissionHandling: true,
                        });
                      } else {
                        setEditedBcr({
                          ...editedBcr,
                          inheritCommissionHandling: false,
                        });
                      }
                    }}
                    label={formatMessage(Messages.commissionPage_inheritCommissionHandling)}
                  />
                )}
                <CheckBox
                  cssClasses="commission-page_edit-bcr_checkboxes"
                  checkBoxId={'useBrokerCost'}
                  checked={editedBcr.useBrokerCost}
                  disabled={editedBcr.inheritCommissionHandling}
                  onChange={() => {
                    setEditedBcr({
                      ...editedBcr,
                      useBrokerCost: !editedBcr.useBrokerCost,
                      rates: getRatesWhenUseBrokerCostChange(editedBcr.rates, !editedBcr.useBrokerCost),
                    });
                  }}
                  label={formatMessage(Messages.commissionPage_useBrokerCost)}
                />
                <CheckBox
                  cssClasses="commission-page_edit-bcr_checkboxes"
                  checkBoxId={'usePersonalCommission'}
                  checked={editedBcr.usePersonalCommission}
                  disabled={editedBcr.inheritCommissionHandling}
                  onChange={() => {
                    setEditedBcr({
                      ...editedBcr,
                      usePersonalCommission: !editedBcr.usePersonalCommission,
                      rates: getRatesWhenUsePersonalCommissionChange(editedBcr.rates, !editedBcr.usePersonalCommission),
                    });
                  }}
                  label={formatMessage(Messages.commissionPage_usePersonalCommission)}
                />
                <CheckBox
                  cssClasses="commission-page_edit-bcr_checkboxes"
                  checkBoxId={'useMotorCommission'}
                  checked={editedBcr.useMotorCommission}
                  disabled={editedBcr.inheritCommissionHandling}
                  onChange={() => {
                    setEditedBcr({
                      ...editedBcr,
                      useMotorCommission: !editedBcr.useMotorCommission,
                      rates: getRatesWhenUseMotorCommissionChange(editedBcr.rates, !editedBcr.useMotorCommission),
                    });
                  }}
                  label={formatMessage(Messages.commissionPage_useMotorCommission)}
                />
              </div>
              <div className="commission-page_edit-bcr-form_rate-column">
                <TextInput
                  {...getInputFieldsProperties(BrokerCostType.Property, editedBcr, setEditedBcr)}
                  disabled={!editedBcr.useBrokerCost || editedBcr.inheritCommissionHandling}
                />
                <TextInput
                  {...getInputFieldsProperties(BrokerCostType.Liability, editedBcr, setEditedBcr)}
                  disabled={!editedBcr.useBrokerCost || editedBcr.inheritCommissionHandling}
                />
                <TextInput
                  {...getInputFieldsProperties(BrokerCostType.Person, editedBcr, setEditedBcr)}
                  disabled={!editedBcr.useBrokerCost || editedBcr.inheritCommissionHandling}
                />
                <TextInput
                  {...getInputFieldsProperties(BrokerCostType.Transport, editedBcr, setEditedBcr)}
                  disabled={!editedBcr.useBrokerCost || editedBcr.inheritCommissionHandling}
                />
                <TextInput
                  {...getInputFieldsProperties(BrokerCostType.Casco, editedBcr, setEditedBcr)}
                  disabled={!editedBcr.useBrokerCost || editedBcr.inheritCommissionHandling}
                />
              </div>

              <div className="commission-page_edit-bcr-form_rate-column">
                <TextInput
                  {...getInputFieldsProperties(BrokerCostType.PersonalCommission, editedBcr, setEditedBcr)}
                  disabled={true}
                />
                <TextInput
                  {...getInputFieldsProperties(BrokerCostType.MotorCommission, editedBcr, setEditedBcr)}
                  disabled={!editedBcr.useMotorCommission || editedBcr.inheritCommissionHandling}
                />
              </div>
            </div>

            <ErrorMessage message={getErrorMessage(editBcrFormId, errors)} />
          </Form>
        </div>
      )}
    </Modal>
  );
};
export default EditBrokerCostRate;
